import { refGetElement } from "@gameApp/helpers/ref";
import { isFunction } from "@gameApp/utils/isFunction";
import { type RefObject, useCallback, useEffect } from "react";

const IS_CLIENT = typeof window !== "undefined";

export type IntersectionObserverHandler = (
  entry: IntersectionObserverEntry,
) => void;

export interface IntersectionObserverInstance {
  observe: (el: HTMLElement, cb: IntersectionObserverHandler) => void;
  unobserve: (el: HTMLElement) => void;
  destroy: () => void;
  useCallbackWhenInView: (params: HookParams) => void;
}

interface HookParams {
  targetRef: null | RefObject<HTMLElement | null>;
  cb: null | IntersectionObserverHandler;
}

export function intersectionObserverCreate(
  init?: IntersectionObserverInit,
): IntersectionObserverInstance {
  const observer: null | IntersectionObserver = IS_CLIENT
    ? new window.IntersectionObserver(onObserve, init)
    : null;

  const callbacksMap = new Map<HTMLElement, IntersectionObserverHandler>();

  function observe(el: HTMLElement, cb: IntersectionObserverHandler) {
    if (observer && el instanceof HTMLElement) {
      observer.observe(el);
      callbacksMap.set(el, cb);
    }
  }

  function unobserve(el: HTMLElement) {
    if (observer && el instanceof HTMLElement) {
      callbacksMap.delete(el);
      observer.unobserve(el);
    }
  }

  function onObserve(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
      if (
        entry.target instanceof HTMLElement &&
        callbacksMap.has(entry.target)
      ) {
        const cb = callbacksMap.get(entry.target);

        if (isFunction(cb)) {
          cb(entry);
        }
      }
    }
  }

  function destroy() {
    if (observer) {
      observer.disconnect();
      callbacksMap.clear();
    }
  }

  function useCallbackWhenInView(params: HookParams) {
    const { targetRef, cb } = params;

    const handleObserve = useCallback(
      (entry: IntersectionObserverEntry) => {
        if (isFunction(cb)) {
          cb(entry);
        }
      },
      [cb],
    );

    useEffect(() => {
      const el = refGetElement(targetRef);

      if (el) {
        observe(el, handleObserve);
      }

      return () => {
        if (el) {
          unobserve(el);
        }
      };
    }, [targetRef, handleObserve]);
  }

  return {
    useCallbackWhenInView,
    observe,
    unobserve,
    destroy,
  };
}
